import { Link as RouterLink } from 'react-router-dom';
import {
  SubmissionStatus,
  type SubmissionTableRow,
} from '../../../../api/submissions/submissions.api.types';
import { AccountGroupType } from '../../../../types/account-groups/account-group.dto';
import type { SelectOption } from '../../../../types/components/common';
import { withShowable } from '../../../_global/lib/withShowable';
import { TruncatedTextWithTooltip } from '../../../dashboard/agency/agency-dashboard.utils';

import { usePersona } from '../../../../components/hooks/usePersona';
import TooltipList from '../../../../shared/TooltipList';
import { getDisplayStatus } from '../../utils';
import { ReadOnlyMarker } from '../../modules/ReadOnlyMarker';
import {
  FlagCell,
  getFlagsColumnConfig,
} from '../../../../components/flags/FlagCell';
import {
  renderFlags,
  type FlagRow,
} from '../../../../components/flags/registry';

const Link = withShowable(RouterLink);

type SubmissionStatusCellProps = {
  submissionStatus: SubmissionStatus;
  declinationReasons: string[] | undefined | null;
  carrierReferralReasons: string[] | undefined | null;
  readonly readOnly?: boolean;
};

export type WorkspaceTableContext = {
  submissionsUsersSelectOptions: SelectOption[];
};

type SubmissionNumberCellProps = {
  submissionId: string;
  text: string;
  length?: number;
};

export const SubmissionLinkCell = ({
  submissionId,
  text,
  length = 40,
}: SubmissionNumberCellProps) => {
  const persona = usePersona();

  const submissionsLink = `${persona.basePath}/submissions/summary/${submissionId}/summary`;

  return (
    <Link show to={submissionsLink}>
      <TruncatedTextWithTooltip text={text} length={length} />
    </Link>
  );
};

export const RenewalSubmissionLinkCell = ({
  submissionId,
  text,
  length = 40,
}: SubmissionNumberCellProps) => {
  const persona = usePersona();
  return (
    <Link
      show
      to={`${persona.basePath}/submissions/renewal/${submissionId}/summary`}
    >
      <TruncatedTextWithTooltip text={text} length={length} />
    </Link>
  );
};

export const SubmissionStatusCell = ({
  submissionStatus,
  declinationReasons,
  carrierReferralReasons,
  readOnly,
}: SubmissionStatusCellProps) => {
  const label = getDisplayStatus(submissionStatus);

  let statusTooltipReasons: Set<string>;
  if (submissionStatus === SubmissionStatus.DECLINED) {
    statusTooltipReasons = new Set(declinationReasons || []);
  } else if (submissionStatus === SubmissionStatus.CARRIER_REFERRAL) {
    statusTooltipReasons = new Set(carrierReferralReasons || []);
  } else {
    statusTooltipReasons = new Set();
  }
  return (
    <div>
      {label}
      {statusTooltipReasons.size > 0 && (
        <TooltipList items={Array.from(statusTooltipReasons)} />
      )}
      {readOnly ? <ReadOnlyMarker /> : null}
    </div>
  );
};

export function getAgentCellValue(row: SubmissionTableRow) {
  const agentFirstName = row?.agencyAccount?.agent?.firstName ?? '';
  const agentLastName = row?.agencyAccount?.agent?.lastName ?? '';
  if (!agentFirstName && !agentLastName) {
    return '-';
  }

  return `${agentFirstName} ${agentLastName}`;
}

export const getFlagsColumn = () =>
  getFlagsColumnConfig({
    // Fixed 104px keeps the flag column compact but gives the two flag icons
    // (and the "Flags" header) breathing room instead of sitting edge-to-edge
    // at 90px. A raw px width is non-flexible, so it no longer stretches to
    // fill on the fill-enabled submissions tables like the default `s` token.
    width: 104,
    getCellValue: (row: SubmissionTableRow) => (
      <FlagCell>{renderFlags(row as unknown as FlagRow)}</FlagCell>
    ),
  });

function getAccountGroupNameIfType(
  row: SubmissionTableRow,
  groupType: AccountGroupType
) {
  if (row.accountGroup?.groupType !== groupType) {
    return '-';
  }
  return row.accountGroup?.name ?? '-';
}

/**
 * Shared `programName`/`poolName` filterable columns, driven by the submission's
 * own `accountGroup` relation. Used by both the Workspace and the shared
 * All Submissions / Agency Submissions listing — kept in one place to avoid
 * duplicating the same column definitions across both `useColumnConfig` files.
 */
export const getAccountGroupColumns = () => ({
  programName: {
    name: 'programName',
    title: 'Program',
    filterType: 'string',
    show: false,
    getCellValue: (row: SubmissionTableRow) =>
      getAccountGroupNameIfType(row, AccountGroupType.PROGRAM),
    csv: (row: SubmissionTableRow) =>
      getAccountGroupNameIfType(row, AccountGroupType.PROGRAM),
    sort: false,
  },
  poolName: {
    name: 'poolName',
    title: 'Pool',
    filterType: 'string',
    show: false,
    getCellValue: (row: SubmissionTableRow) =>
      getAccountGroupNameIfType(row, AccountGroupType.POOL),
    csv: (row: SubmissionTableRow) =>
      getAccountGroupNameIfType(row, AccountGroupType.POOL),
    sort: false,
  },
});
